import { Checkbox } from "@/components/ui/checkbox"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router"
import {
  FINE_STATUSES,
  INQUIRY_STATUS_DISPLAY,
  toFineStatusList,
  toInquiryBucket,
  toInquiryTrigger,
  useInquiryList,
  useInquiryMatchCandidates,
  useInquiryStats,
  type InquiryBucket,
  type InquiryMatchCandidate,
  type SupplierInquiryListItem,
} from "../../../api/supplierInquiriesApi"
import { formatDate } from "../../../utils/date"
import {
  INQUIRY_FILTER_KEYS,
  inquiryRegulationLabels,
} from "../../../utils/inquiryScope"
import {
  MasterDataFilterChips,
  MasterDataFilterToggle,
  useMasterDataFiltersState,
  type MasterDataFacet,
} from "../../master-data/MasterDataFilters"
import { MasterDataList } from "../../master-data/MasterDataList"
import { MasterDataListMeta } from "../../master-data/MasterDataListMeta"
import { MasterDataSearch } from "../../master-data/MasterDataSearch"
import {
  MasterDataSort,
  type MasterDataSortValue,
} from "../../master-data/MasterDataSort"
import { useDefaultRowSelection } from "../../master-data/useDefaultRowSelection"
import { EntityListRow } from "../../shared/EntityListRow"
import { EntityListRowContent } from "../../shared/EntityListRowContent"
import { FilterTabs, type FilterTabOption } from "../../shared/FilterTabs"
import { McPill } from "../../shared/McPill"
import { InquiryScopePills } from "./InquiryScopePills"
import { MatchCandidateRow } from "./MatchCandidatePanel"
import { ReplyPendingPill, replyPendingKind } from "./ReplyPendingPill"

interface InquiryListPanelProps {
  selectedIds: Set<string>
  // Always with the row: the bulk panel renders and sends from the selection's
  // row snapshots (useRowMultiSelect.selectedRows), so a toggle without one
  // shows as checked but is never sent.
  onToggleSelected: (id: string, row: SupplierInquiryListItem) => void
  onSelectAll: (rows: SupplierInquiryListItem[]) => void
  onClearAll: () => void
}

type InquiryListView = InquiryBucket | "matching"

export function InquiryListPanel({
  selectedIds,
  onToggleSelected,
  onSelectAll,
  onClearAll,
}: InquiryListPanelProps) {
  const { translate } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const selectedId = searchParams.get("id")
  const statusParam = searchParams.get("status")
  // `status` carries either a bucket (own tabs) or a fine-status list (dashboard
  // deep-links, and the Open tab's facet below). toInquiryBucket falls back to "open"
  // for a fine list, so the right tab shows active without extra state.
  const fineStatuses = toFineStatusList(statusParam)
  const bucket = toInquiryBucket(statusParam)
  // Dashboard deep-link only (GapsWidget's staged-inquiries row): ANDs onto
  // whichever of bucket/status above is active. Own tabs never set this —
  // handleTabChange clears it below. toInquiryTrigger degrades a stale/
  // hand-edited value to "no filter" instead of sending the backend a
  // trigger_type it 400s on.
  const triggerType = toInquiryTrigger(searchParams.get("trigger_type"))
  // Regelwerk filter, comma list of canonical keys. Unknown entries of a stale or
  // hand-edited URL are dropped rather than sent (the backend 422s on them).
  const regulations = (searchParams.get("regulation") ?? "")
    .split(",")
    .filter((key) => (INQUIRY_FILTER_KEYS as readonly string[]).includes(key))
  const regulation = regulations.length > 0 ? regulations.join(",") : undefined
  const view: InquiryListView = statusParam === "matching" ? "matching" : bucket
  const isMatchingView = view === "matching"

  const [search, setSearch] = useState("")
  const debouncedSearch = useDebouncedValue(search, 300)
  // "created_desc" (newest-first) reproduces the list's original hardcoded order.
  const [sort, setSort] = useState<MasterDataSortValue>("created_desc")

  const { data: stats } = useInquiryStats()
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
    isPlaceholderData,
  } = useInquiryList(
    fineStatuses
      ? {
          status: fineStatuses.join(","),
          triggerType,
          regulation,
          q: debouncedSearch || undefined,
          sort,
          limit: 50,
          enabled: !isMatchingView,
        }
      : {
          bucket,
          triggerType,
          regulation,
          q: debouncedSearch || undefined,
          sort,
          limit: 50,
          enabled: !isMatchingView,
        },
  )
  const {
    data: matchesPage,
    isLoading: matchesLoading,
    hasNextPage: hasMoreMatches,
    fetchNextPage: fetchMoreMatches,
    isFetchingNextPage: isFetchingMoreMatches,
  } = useInquiryMatchCandidates(isMatchingView, debouncedSearch)
  const matchCandidates = matchesPage?.pages.flatMap((page) => page.items) ?? []

  // Rows arrive pre-ordered by `sort` from the backend.
  const rows = data?.pages.flatMap((p) => p.items) ?? []
  const totalCount = data?.pages[0]?.total

  // As in Master Data: keeps `?id=` set, which is also what keeps the detail pane
  // expanded (MasterDataPanelLayout keys off that param alone). Held back while the
  // rows are the previous tab's placeholder data, so switching tabs can't re-seed
  // `?id=` from the tab we just left.
  useDefaultRowSelection(rows, selectedId, setSearchParams, !isPlaceholderData)

  // Drafts only — nothing else can be sent/discarded. Select-all covers the loaded rows.
  const isDraftBucket = bucket === "draft"
  const draftRows = isDraftBucket ? rows : []
  const allSelected =
    draftRows.length > 0 && draftRows.every((r) => selectedIds.has(r.id))
  const toggleAll = () => (allSelected ? onClearAll() : onSelectAll(draftRows))

  // Leaving the draft tab drops the selection, so the bulk bar can't linger.
  useEffect(() => {
    if (!isDraftBucket) onClearAll()
  }, [isDraftBucket, onClearAll])

  // Open tab only — draft/closed have no sub-statuses. Reuses the existing `status`
  // param, so no new URL shape or backend work.
  const fineStatusFacet: MasterDataFacet = {
    key: "fineStatus",
    title: translate("MC_MD_FILTER_STATUS_TITLE"),
    options: FINE_STATUSES.map((status) => ({
      value: status,
      label: translate(INQUIRY_STATUS_DISPLAY[status].labelKey),
    })),
    selected: fineStatuses ?? [],
    onSelectionChange: (values) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (values.length > 0) next.set("status", values.join(","))
        else next.delete("status")
        return next
      })
    },
  }
  // Every tab: what an inquiry asks is independent of its status.
  const regulationFacet: MasterDataFacet = {
    key: "regulation",
    title: translate("MC_INQ_FILTER_REGULATION"),
    options: inquiryRegulationLabels(INQUIRY_FILTER_KEYS, translate).map(
      (option) => ({ value: option.key, label: option.title }),
    ),
    selected: regulations,
    onSelectionChange: (values) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (values.length > 0) next.set("regulation", values.join(","))
        else next.delete("regulation")
        return next
      })
    },
  }
  const facets: MasterDataFacet[] = isMatchingView
    ? []
    : bucket === "open"
      ? [fineStatusFacet, regulationFacet]
      : [regulationFacet]
  const {
    isOpen: filtersOpen,
    setIsOpen: setFiltersOpen,
    hasActiveFilters,
  } = useMasterDataFiltersState(facets)

  function clearFilters() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (fineStatuses) next.delete("status")
      next.delete("regulation")
      return next
    })
  }

  // reference · created date, present parts only (the scope shows as pills beside
  // it). Created date shows because it's the default sort key; the response-due date
  // lives in the detail panel.
  function rowMeta(row: (typeof rows)[number]): string {
    const parts: string[] = []
    if (row.reference) parts.push(row.reference)
    const created = formatDate(row.createdAt ?? row.sentAt)
    if (created) parts.push(created)
    return parts.join(" · ") || "—"
  }

  function withCount(label: string, count?: number) {
    return count === undefined ? label : `${label} (${count})`
  }

  const STATUS_TABS: FilterTabOption<InquiryListView>[] = [
    {
      value: "draft",
      label: withCount(translate("MC_INQ_STATUS_DRAFT"), stats?.drafts),
    },
    {
      value: "open",
      label: withCount(translate("MC_INQ_STATUS_OPEN"), stats?.open),
    },
    {
      value: "closed",
      label: withCount(translate("MC_INQ_STATUS_CLOSED"), stats?.closed),
    },
    {
      // Always shown, and counted only once opened: the number used to come from
      // get_stats, which had to group inbound mail against its declarations on every
      // page load just to decide whether to render this tab.
      value: "matching",
      label: translate("MC_INQ_STATUS_NEEDS_MATCHING"),
    },
  ]

  function handleTabChange(value: InquiryListView) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("status", value)
      next.delete("id")
      // An explicit tab click leaves whatever dashboard deep-link brought
      // trigger_type along (e.g. GapsWidget's staged-inquiries row) — it
      // has no chip/facet of its own, so leaving it set would silently
      // filter the tab the user just switched to.
      next.delete("trigger_type")
      return next
    })
  }

  // Drafts included: the row opens the detail panel, which offers "Open draft".
  function openRow(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("id", id)
      return next
    })
  }

  function handleRowClick(row: SupplierInquiryListItem) {
    openRow(row.id)
    // Mid-selection, a row click also toggles it — as EntityListPanel does.
    if (isDraftBucket && selectedIds.size > 0) {
      onToggleSelected(row.id, row)
    }
  }

  return (
    <div className="flex h-full flex-col">
      <MasterDataSearch
        value={search}
        onChange={setSearch}
        placeholder={translate(
          isMatchingView
            ? "MC_INQ_MATCH_SEARCH_PLACEHOLDER"
            : "MC_INQ_SEARCH_PLACEHOLDER",
        )}
        loading={!!search && (isMatchingView ? matchesLoading : isFetching)}
        sort={
          isMatchingView ? undefined : (
            <MasterDataSort
              value={sort}
              onChange={setSort}
              defaultValue="created_desc"
            />
          )
        }
        filterToggle={
          facets.length > 0 && (
            <MasterDataFilterToggle
              isOpen={filtersOpen}
              onToggle={() => setFiltersOpen((current) => !current)}
              hasActiveFilters={hasActiveFilters}
            />
          )
        }
        filterChips={
          filtersOpen &&
          facets.length > 0 && (
            <MasterDataFilterChips
              facets={facets}
              hasActiveFilters={hasActiveFilters}
              onClearAll={clearFilters}
            />
          )
        }
      />

      <FilterTabs
        options={STATUS_TABS}
        value={view}
        onValueChange={handleTabChange}
      />

      {!isMatchingView && isDraftBucket && rows.length > 0 && (
        // Not a <label>: Checkbox is a Radix button, so a label wouldn't forward clicks.
        <div className="flex items-center gap-2 border-b border-grey4 px-4 py-2">
          <Checkbox
            checked={allSelected}
            onCheckedChange={toggleAll}
            aria-label={translate(
              allSelected
                ? "MC_INQ_BULK_DESELECT_ALL"
                : "MC_INQ_BULK_SELECT_ALL",
            )}
          />
          <button
            type="button"
            onClick={toggleAll}
            className="text-xs text-grey5 hover:text-white"
          >
            {selectedIds.size > 0 && totalCount !== undefined ? (
              <MasterDataListMeta
                selectedCount={selectedIds.size}
                totalCount={totalCount}
              />
            ) : (
              translate("MC_INQ_BULK_SELECT_ALL")
            )}
          </button>
        </div>
      )}

      {isMatchingView ? (
        <NeedsMatchingList
          candidates={matchCandidates}
          isLoading={matchesLoading}
          selectedId={selectedId}
          onSelect={openRow}
          hasNextPage={hasMoreMatches}
          onFetchMore={fetchMoreMatches}
          isFetchingNextPage={isFetchingMoreMatches}
        />
      ) : (
        <MasterDataList
          rows={rows}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onFetchMore={fetchNextPage}
          selectedKey={selectedId ?? undefined}
          renderRow={(row) => {
            const display = INQUIRY_STATUS_DISPLAY[row.status]
            const pendingKind = replyPendingKind(row)
            return (
              <EntityListRow
                key={row.id}
                rowId={row.id}
                isSelected={row.id === selectedId}
                hasFlag={pendingKind !== null}
                onClick={() => handleRowClick(row)}
                checkbox={
                  isDraftBucket ? (
                    <Checkbox
                      checked={selectedIds.has(row.id)}
                      onCheckedChange={() => onToggleSelected(row.id, row)}
                      aria-label={translate("MC_INQ_SELECT_ROW")}
                    />
                  ) : undefined
                }
              >
                <EntityListRowContent
                  title={row.supplierName ?? "—"}
                  meta={
                    // The scope pills follow the reference and date on the meta
                    // line, left-aligned and wrapping, not pushed to the right.
                    <>
                      <span>{rowMeta(row)}</span>
                      <InquiryScopePills inquiry={row} max={3} />
                    </>
                  }
                />
                {/* Pills sit beside the two-line content block, vertically
                    centred on the row rather than on the title line. */}
                <span className="flex shrink-0 items-center gap-1.5 self-center">
                  {pendingKind && <ReplyPendingPill kind={pendingKind} />}
                  {display && (
                    <McPill tone={display.tone}>
                      {translate(display.labelKey)}
                    </McPill>
                  )}
                </span>
              </EntityListRow>
            )
          }}
        />
      )}
    </div>
  )
}

function NeedsMatchingList({
  candidates,
  isLoading,
  selectedId,
  onSelect,
  hasNextPage,
  onFetchMore,
  isFetchingNextPage,
}: {
  candidates: InquiryMatchCandidate[]
  isLoading: boolean
  selectedId: string | null
  onSelect: (id: string) => void
  hasNextPage: boolean
  onFetchMore: () => void
  isFetchingNextPage: boolean
}) {
  const { translate } = useTranslation()

  // Keep a real candidate selected so the review panel is never empty next to a populated
  // list — same contract as useDefaultRowSelection on the other tabs. Re-seeds when the
  // selected id is GONE too, not just absent: connecting or dismissing removes that
  // candidate from the refetched list, which would otherwise strand `?id=` on it.
  useEffect(() => {
    if (isLoading || candidates.length === 0) return
    if (selectedId && candidates.some((c) => c.candidateId === selectedId))
      return
    onSelect(candidates[0].candidateId)
  }, [isLoading, selectedId, candidates, onSelect])

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-grey5">
        {translate("MC_INQ_MATCH_LOADING")}
      </div>
    )
  }
  if (candidates.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-grey5">
        {translate("MC_INQ_MATCH_EMPTY")}
      </div>
    )
  }

  // Same infinite-scroll shell the other tabs use, so paging behaves identically.
  return (
    <MasterDataList
      rows={candidates.map((candidate) => ({
        ...candidate,
        id: candidate.candidateId,
      }))}
      isLoading={isLoading}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onFetchMore={onFetchMore}
      selectedKey={selectedId ?? undefined}
      renderRow={(row) => (
        <EntityListRow
          isSelected={row.id === selectedId}
          onClick={() => onSelect(row.id)}
        >
          <MatchCandidateRow candidate={row} />
        </EntityListRow>
      )}
    />
  )
}
